import {
  ProjectGeoDataByIri,
  ProjectGeoDataProperty,
} from 'src/constants/projectGeoData';
import { GEO_DATA_KEY_BY_IRI } from 'src/templates/queries/geoData';
import { DESIGNER_COLOR_ID_IRI } from 'src/templates/queries/iris';
import { JsonLdResult } from 'src/templates/queries/types';
import { getJsonLdResultDescription } from 'src/utils/jsonld/getJsonLdResultDescription';
import { getJsonLdResultLabel } from 'src/utils/jsonld/getJsonLdResultLabel';
import { getJsonLdResultLocalName } from 'src/utils/jsonld/getJsonLdResultLocalName';
import { getJsonLdResultValue } from 'src/utils/jsonld/getJsonLdResultValue';

export const parseMetadataResults = (rawResults: JsonLdResult[]) => {
  const colorIdsByIri: Record<string, string> = {};
  const descriptionsByIri: Record<string, string> = {};
  const geoDataByIri: Record<string, ProjectGeoDataByIri> = {};
  const labelsByIri: Record<string, string> = {};
  rawResults.forEach((result) => {
    // continue if the result is missing an iri
    const iri = result['@id'];
    if (typeof iri !== 'string') {
      return;
    }

    const colorValue =
      result[DESIGNER_COLOR_ID_IRI] !== undefined &&
      getJsonLdResultValue(
        result[DESIGNER_COLOR_ID_IRI],
        (_, idx) => !idx // only get first color
      );
    if (colorValue && !colorIdsByIri[iri]) {
      colorIdsByIri[iri] = colorValue;
    }

    const descriptionValue = getJsonLdResultDescription(result);
    if (descriptionValue && !descriptionsByIri[iri]) {
      descriptionsByIri[iri] = descriptionValue;
    }

    // only set the new label if there isn't an existing label
    // or the existing label was a fallback to the local name
    const labelValue = getJsonLdResultLabel(result);
    if (
      labelValue &&
      (!labelsByIri[iri] ||
        labelsByIri[iri] === getJsonLdResultLocalName(result))
    ) {
      labelsByIri[iri] = labelValue;
    }

    // Parse geo data properties
    const geoData: ProjectGeoDataByIri = {};

    // Helper function to parse geo property and determine if it's an attribute reference
    const parseGeoProperty = (
      property: JsonLdResult[string]
    ): ProjectGeoDataProperty | null => {
      if (property === undefined) {
        return null;
      }

      const propertyData = Array.isArray(property) ? property[0] : property;
      if (typeof propertyData === 'string') {
        return { value: propertyData, isAttribute: false };
      }

      if (typeof propertyData === 'object' && propertyData !== null) {
        if (
          '@value' in propertyData &&
          typeof propertyData['@value'] === 'string'
        ) {
          return { value: propertyData['@value'], isAttribute: false };
        }

        if ('@id' in propertyData && typeof propertyData['@id'] === 'string') {
          return { value: propertyData['@id'], isAttribute: true };
        }
      }

      return null;
    };

    // Process each geo property
    Object.entries(GEO_DATA_KEY_BY_IRI).forEach(([iri, propertyName]) => {
      const propertyData = parseGeoProperty(result[iri]);
      if (propertyData) {
        geoData[propertyName as keyof ProjectGeoDataByIri] = propertyData;
      }
    });

    // Only add to geoDataByIri if we have at least one geo property
    if (Object.keys(geoData).length > 0) {
      geoDataByIri[iri] = geoData;
    }
  });
  return { colorIdsByIri, descriptionsByIri, geoDataByIri, labelsByIri };
};
